import { formatMinutesToHoursString } from "@components/inputs/date/utils";
import { IFieldDef, IGetValueArgs } from "@components/smart/FieldInfo";
import { createPath } from "@odata/BindingContext";
import {
    IPrPayslipEmploymentEntity,
    IPrPayslipEmploymentItemEntity,
    IPrPayslipEmploymentSubitemEntity,
    IPrPayslipEntity,
    IPrPayslipItemEntity,
    IPrPayslipSubitemEntity,
    PrDeductionEntity,
    PrEmploymentRetirementSavingEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEmploymentItemEntity,
    PrPayslipEmploymentSubitemEntity,
    PrPayslipEntity,
    PrPayslipItemEntity,
    PrPayslipItemTypeEntity,
    PrPayslipSubitemEntity,
    PrRetirementSavingsCompanyEntity,
    PrRetirementSavingsProductEntity,
    PrSalaryAdvanceEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrPayslipEmploymentItemTypeCode,
    PrPayslipEmploymentSubitemTypeCode,
    PrPayslipItemTypeCode,
    PrPayslipSubitemTypeCode
} from "@odata/GeneratedEnums";
import { getPayrollCurrencyFormatter, getPayrollCurrencyUnit } from "@pages/payroll/payroll.utils";
import {
    IEditableInfoDef,
    IFormTableListAdditionalColumn,
    IFormTableListRow,
    IFormTableListSection,
    TFormTableListValue
} from "@pages/payroll/payslip/formTableList/FormTableList";
import {
    getTotalOverviewEmploymentSectionsDef,
    getTotalOverviewPayslipSectionsDef
} from "@pages/payroll/payslip/overview/TotalOverview.def";
import { dayFormatter, getEmploymentName, hoursFormatter } from "@pages/payroll/payslip/Payslip.utils";
import { formatPercent, isDefined, isNotDefined } from "@utils/general";

import { EMPTY_DASH } from "../../../../constants";
import DateType, { DateFormat } from "../../../../types/Date";
import { FormStorage } from "../../../../views/formView/FormStorage";

export enum RowValueType {
    Currency,
    Percentage,
    Date,
    Days,
    Hours,
    HoursFromMinutes,
    CurrencyPerHour,
    String
}

export type TPayslipItem = IPrPayslipItemEntity | IPrPayslipEmploymentItemEntity;
export type TPayslipSubItem = IPrPayslipSubitemEntity | IPrPayslipEmploymentSubitemEntity;
export type TPayslipItemType = PrPayslipItemTypeCode | PrPayslipEmploymentItemTypeCode | PrPayslipSubitemTypeCode | PrPayslipEmploymentSubitemTypeCode;
export type TFilterSubitemsFn = (defs: TPayslipSubItemDef[], args: ITotalOverviewArgs) => TPayslipSubItemDef[];

export type TPayslipSubItemDef = Omit<IPayslipOverviewEditableRowDef, "subItems"> | Omit<IPayslipOverviewReadOnlyRowDef, "subItems">;
export type TPayslipOverviewFormatterFn = (item: TPayslipItem, valueTypeFormatter: ((value: number) => string), value: TFormTableListValue, args: ITotalOverviewArgs) => string | React.ReactElement;

// common config props
interface IPayslipOverviewRowDef {
    id?: TPayslipItemType | TPayslipItemType[] | string;
    isHeader?: boolean;
    isSubRow?: boolean;
    useTypeName?: boolean;
    label?: string | ((item: TPayslipItem) => string);
    visibleIfContains?: string[];
    filterSubItems?: TFilterSubitemsFn; // used when subitems visibility is based on some condition, so we need to filter them in getFormTableListAdditionalColumnsFromSubItems
    subItems?: TPayslipSubItemDef[];
    valueType?: RowValueType;
    formatter?: TPayslipOverviewFormatterFn;
    isVisible?: (storage: FormStorage, args: ITotalOverviewArgs) => boolean;
}

interface IPayslipOverviewEditableRowDef extends IPayslipOverviewRowDef {
    headerRow?: Omit<TPayslipOverviewRowDef, "headerRow">;
}

interface IPayslipOverviewReadOnlyRowDef extends IPayslipOverviewRowDef {
    value?: number | string;
}

export type TPayslipOverviewRowDef = IPayslipOverviewEditableRowDef | IPayslipOverviewReadOnlyRowDef;

export function isEditableRowDef(rowDef: TPayslipOverviewRowDef): rowDef is IPayslipOverviewEditableRowDef {
    return isNotDefined((rowDef as IPayslipOverviewReadOnlyRowDef).value) && !(rowDef as IPayslipOverviewReadOnlyRowDef).formatter;
}

/**
 * Get additional properties for form definition
 */
export function getTotalOverviewEmploymentAdditionalProperties(): IFieldDef[] {
    return [{
        id: PrPayslipEmploymentEntity.Items,
        additionalProperties: [
            {
                id: PrPayslipEmploymentItemEntity.RetirementSaving,
                additionalProperties: [{ id: createPath(PrEmploymentRetirementSavingEntity.RetirementSavingsProduct, PrRetirementSavingsProductEntity.PrRetirementSavingsCompany, PrRetirementSavingsCompanyEntity.Name) }]
            },
            { id: PrPayslipEmploymentItemEntity.Name },
            { id: PrPayslipEmploymentItemEntity.TypeCode },
            { id: createPath(PrPayslipEmploymentItemEntity.Type, PrPayslipItemTypeEntity.Name) },
            { id: PrPayslipEmploymentItemEntity.DecimalValue },
            { id: PrPayslipEmploymentItemEntity.IntValue },
            { id: PrPayslipEmploymentItemEntity.Percentage },
            {
                id: PrPayslipEmploymentItemEntity.Subitems,
                additionalProperties: [
                    { id: PrPayslipEmploymentSubitemEntity.TypeCode },
                    { id: createPath(PrPayslipEmploymentSubitemEntity.Type, PrPayslipItemTypeEntity.Name) },
                    { id: PrPayslipEmploymentSubitemEntity.DecimalValue },
                    { id: PrPayslipEmploymentSubitemEntity.StringValue },
                    { id: PrPayslipEmploymentSubitemEntity.DateValue },
                    { id: PrPayslipEmploymentSubitemEntity.IntValue },
                ]
            },
        ]
    }];
}
export function getTotalOverviewAdditionalProperties(): IFieldDef[] {
    return [{
        id: PrPayslipEntity.Items,
        additionalProperties: [
            { id: PrPayslipItemEntity.Name },
            { id: PrPayslipItemEntity.TypeCode },
            {
                id: PrPayslipItemEntity.SalaryAdvance,
                additionalProperties: [{
                    id: PrSalaryAdvanceEntity.NumberOurs
                }, {
                    id: PrSalaryAdvanceEntity.Id
                }]
            },
            { id: createPath(PrPayslipItemEntity.Type, PrPayslipItemTypeEntity.Name) },
            { id: createPath(PrPayslipItemEntity.Deduction, PrDeductionEntity.Type ) },
            { id: PrPayslipItemEntity.DecimalValue },
            {
                id: PrPayslipItemEntity.Subitems,
                additionalProperties: [
                    { id: PrPayslipSubitemEntity.TypeCode },
                    { id: createPath(PrPayslipSubitemEntity.Type, PrPayslipItemTypeEntity.Name) },
                    { id: PrPayslipSubitemEntity.DecimalValue },
                    { id: PrPayslipSubitemEntity.IntValue },
                ]
            },
        ]
    }];
}

function getPropNameFromType<T extends TPayslipItem>(type: RowValueType): { propName: keyof T; overridePropName?: keyof T } {
    let prop: string;
    switch (type) {
        case RowValueType.Currency:
        case RowValueType.CurrencyPerHour:
        case RowValueType.Percentage:
            prop = "DecimalValue"; break;
        case RowValueType.String:
            prop = "StringValue"; break;
        case RowValueType.Date:
            prop = "DateValue"; break;
        default:
            prop = "IntValue"; break;
    }

    const overridePropName = `${prop}Override` as keyof T;
    const propName = prop as keyof T;
    return {
        overridePropName,
        propName
    };
}

function getFormatterFromType(type: RowValueType, args: ITotalOverviewArgs): (value: any) => string {
    switch (type) {
        case RowValueType.Currency:
            return args.currencyFormatter;
        case RowValueType.CurrencyPerHour:
            return (val: number) => `${args.currencyFormatter(val)}/h`;
        case RowValueType.Percentage:
            return (val: number) => formatPercent(val, { decimalPlaces: 2 });
        case RowValueType.Days:
            return dayFormatter;
        case RowValueType.Hours:
            return hoursFormatter;
        case RowValueType.HoursFromMinutes:
            return formatMinutesToHoursString;
        case RowValueType.Date:
            return (val: Date) => DateType.format(val, DateFormat.dayAndMonth);
        default:
            return (value) => value.toString();
    }
}

function getUnitFromType(type: RowValueType, args: ITotalOverviewArgs): string {
    switch (type) {
        case RowValueType.Currency:
            return args?.currencyUnit;
        case RowValueType.CurrencyPerHour:
            return `${args?.currencyUnit}/h`;
        case RowValueType.Percentage:
            return "%";
        case RowValueType.Days:
            return "d";
        case RowValueType.Hours:
            return "h";
        case RowValueType.HoursFromMinutes:
            return "min";
        default:
            return "";
    }
}

function getFormTableListAdditionalColumnsFromSubItems(list: TPayslipSubItem[], defs: TPayslipSubItemDef[], args: ITotalOverviewArgs): IFormTableListAdditionalColumn[] {
    const columns: IFormTableListAdditionalColumn[] = [];
    if (!defs?.length) {
        return columns;
    }

    if (typeof args.def?.filterSubItems === "function") {
        defs = args.def.filterSubItems(defs, args);
    }

    defs.forEach((def, idx) => {
        const [row] = getFormTableListRows(list, def, args);
        columns.push({
            id: isEditableRowDef(def) && !Array.isArray(def.id) ? def.id : `idx-${idx}`,
            // todo: may be custom alignment?
            value: row?.value ?? EMPTY_DASH,
            formatter: row?.formatter
        });
    });
    return columns;
}

function getFromTableListRowFromItemAndDef(itemOrSubItem: TPayslipItem | TPayslipSubItem, def: TPayslipOverviewRowDef, args: ITotalOverviewArgs): IFormTableListRow {
    let value, overrideValue, formatter, editableInfo: IEditableInfoDef;

    // TPayslipSubItem is not fully compatible with TPayslipItem, but we don't use it
    // in incompatible way, so we can cast it
    let item = itemOrSubItem as unknown as TPayslipItem;
    const labelFromDef = typeof def.label === "function" ? def.label(item) : def.label;
    const title = labelFromDef ?? (def?.useTypeName ? item.Type?.Name : item?.Name ?? item?.Type?.Name);
    const { isHeader, isSubRow } = def;

    if (isEditableRowDef(def)) {
        const valueType = def.valueType ?? RowValueType.Currency;
        const { propName, overridePropName } = getPropNameFromType(valueType);

        overrideValue = item[overridePropName];
        value = (overrideValue ?? item[propName]) as TFormTableListValue;
        formatter = getFormatterFromType(valueType, args);
        editableInfo = {
            Id: item.Id,
            path: args.employment ? `Employments(${args.employment.Id})` : null,
            propName: overridePropName,
            origValue: item[propName] as number,
            unit: getUnitFromType(valueType, args)
        };
    } else {
        // readonly row
        value = def.value;
        // different default valueType for readonly row
        formatter = isDefined(def.valueType) && getFormatterFromType(def.valueType, args);
    }

    item = args.item ?? item;
    return {
        title,
        value,
        formatter: def.formatter ? def.formatter.bind(null, item, formatter, value, args) : formatter,
        isMarked: !!overrideValue,
        isHeader,
        isSubRow: isSubRow || def.subItems?.length > 0,
        additionalColumns: getFormTableListAdditionalColumnsFromSubItems(item?.Subitems, def.subItems, { ...args, item, def }),
        editableInfo
    };
}

function getFormTableListRows<T extends (TPayslipItem | TPayslipSubItem)>(list: T[], rowDef: TPayslipOverviewRowDef, args: ITotalOverviewArgs): IFormTableListRow[] {
    const generatedRows: IFormTableListRow[] = [];

    // we can add additional visibility condition -> check if there are another rows in the list to show this one
    if (rowDef.visibleIfContains) {
        const item = list.find((i) => rowDef.visibleIfContains.includes(i.TypeCode));
        if (!item) {
            return generatedRows;
        }
    }

    if (isEditableRowDef(rowDef)) {
        const types = Array.isArray(rowDef.id) ? rowDef.id : [rowDef.id];
        const filteredItems = list.filter((i) => types.includes(i.TypeCode));

        if (!filteredItems.length) {
            return generatedRows;
        }
        const passArgs = { ...args, filteredItems };

        if (rowDef.headerRow) {
            const [headerRow] = getFormTableListRows(list, { ...rowDef.headerRow, isHeader: true, isSubRow: true }, passArgs);
            if (headerRow) {
                generatedRows.push(headerRow);
            }
        }

        filteredItems.forEach(item => {
            generatedRows.push(getFromTableListRowFromItemAndDef(item, rowDef, passArgs));
        });
    } else {
        // header row has no filtered items, but we need to do some visibility check, so pass all items in list as filteredItems in args
        const passArgs = { ...args, filteredItems: list };
        generatedRows.push(getFromTableListRowFromItemAndDef(null, rowDef, passArgs));
    }

    return generatedRows;
}

export interface ITotalOverviewArgs {
    currencyUnit: string;
    currencyFormatter: (value: number, minorUnit?: number) => string;
    item?: TPayslipItem; // subitems need access to parent item, so it can be passed to formatter (additional columns)
    payslipItems?: IPrPayslipItemEntity[];
    employment?: IPrPayslipEmploymentEntity; // in case of employment item
    filteredItems?: (TPayslipItem | TPayslipSubItem)[]; // pass all the filtered items to args, so subitems could be transformed based on all items in group, not only the current one
    def?: TPayslipOverviewRowDef; // in case when subitem needs to know about its definition (e.g. for visibility condition based on type)
}

function processRows(rowDefs: TPayslipOverviewRowDef[], items: (IPrPayslipItemEntity | IPrPayslipEmploymentItemEntity)[], args: ITotalOverviewArgs, storage: FormStorage<IPrPayslipEntity>): IFormTableListRow[] {
    const rows: IFormTableListRow[] = [];

    rowDefs
        .filter(r => !isDefined(r.isVisible) || r.isVisible(storage, args) === true)
        .forEach((rowDef) => {
            rows.push(...getFormTableListRows(items, rowDef, args));
        });

    return rows;
}

function createPayslipSections(items: IPrPayslipItemEntity[], args: ITotalOverviewArgs, storage: FormStorage<IPrPayslipEntity>): IFormTableListSection[] {
    const def = getTotalOverviewPayslipSectionsDef();
    return def.map((sectionDef) => {
        return {
            rows: processRows(sectionDef.rows, items, args, storage)
        };
    });
}

function createEmploymentSection(args: ITotalOverviewArgs, storage: FormStorage<IPrPayslipEntity>): IFormTableListSection {
    const def = getTotalOverviewEmploymentSectionsDef();
    return {
        title: getEmploymentName(args.employment),
        rows: processRows(def.rows, args.employment.Items, args, storage)
    };
}

export function getTotalOverviewSections(storage: FormStorage<IPrPayslipEntity>): IFormTableListSection[] {
    const { Employments, Items } = storage.data.entity;
    const getValueArgs: IGetValueArgs = { storage, context: storage.context };

    const args: ITotalOverviewArgs = {
        currencyUnit: getPayrollCurrencyUnit()(getValueArgs),
        payslipItems: Items,
        currencyFormatter: (value) => getPayrollCurrencyFormatter(null, true)(value, { storage, context: storage.context })
    };
    const sections: IFormTableListSection[] = [];

    Employments.forEach((employment => {
        sections.push(createEmploymentSection({ ...args, employment }, storage));
    }));

    sections.push(...createPayslipSections(Items, args, storage));
    return sections.filter((s) => s.rows.length > 0);
}
